/** TEMPORARY debug dialog: every raw input Sentio fed the Mission Agent engine
 *  for this device (headless envelopes and pulled trend rows), newest first,
 *  polled every few seconds while the dialog is open. Backed by
 *  missionagent/integration/debug_inputs.py; remove both together when done.
 *  Click a row for the full JSON. */

import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { useCookies } from "react-cookie";

import * as CONSTANTS from "../../../utils/constants/constants";
import { errorMessage } from "../api";
import type { ActivityRow } from "../types";
import { ActivityFeed } from "./ActivityFeed";
import { TOP_CONTROL_HEIGHT } from "./MissionPicker";

const POLL_MS = 3000;

type DebugInput = {
  kind: "envelope" | "trend";
  received_at: string;
  body: Record<string, any>;
};

type Filter = "all" | "events" | "trend";

const Switch = styled(ToggleButtonGroup)(({ theme }) => ({
  "& .MuiToggleButton-root": {
    ...theme.typography.overline,
    letterSpacing: "0.08em",
    padding: theme.spacing(0.25, 1),
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  },
  "& .MuiToggleButton-root.Mui-selected": {
    color: theme.palette.primary.main,
    background: theme.palette.background.default,
  },
}));

/** What each input is and what the engine does with it (mapping.py). */
const LEGEND: [string, string][] = [
  ["message_event_received", "A device event: msg (typed text), defib (the device's event log: buttons, drugs, alarms, shocks), mission (mission buttons: receiving facility, End Mission), restingecg (PDF), image. Recorded as a device.event fact."],
  ["resting_ecg_received", "Second notice for a resting ECG. Not recorded; its message event already is."],
  ["image_received", "A fully assembled image. Recorded as a device.event with the file name."],
  ["device_status_received", "Status heartbeat every few seconds. Not recorded; it triggers the trend pull."],
  ["patient_info_updated", "Patient metadata changed. Not recorded; age and sex come from trend rows."],
  ["intervention_id_changed", "A new mission on the device. Odd's worker resets the page; the engine ignores it."],
  ["trend row", "A stored trend packet the engine pulled (at most once a minute). One vital sample per parameter."],
];

const Legend = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "max-content 1fr",
  columnGap: theme.spacing(2),
  rowGap: theme.spacing(0.5),
  fontSize: "0.75rem",
  marginBottom: theme.spacing(2),
  "& code": { fontSize: "0.72rem", color: theme.palette.text.primary },
  "& span": { color: theme.palette.text.secondary },
}));

function trendDetail(body: Record<string, any>): string {
  const values = body?.data?.data ?? {};
  return Object.entries(values)
    .filter(([key, value]) => key !== "timestamp" && value !== "" && value !== "-" && typeof value !== "object")
    .map(([key, value]) => `${key} ${value}`)
    .join(" · ");
}

function envelopeDetail(body: Record<string, any>): string | null {
  const parts = [
    body.intervention_id ? `intervention ${body.intervention_id}` : "no intervention",
    body.event_type_name,
    typeof body.event_data === "string" ? body.event_data : body.event_data ? JSON.stringify(body.event_data) : null,
    body.file_name,
    body.device_status !== undefined ? `status ${body.device_status}` : null,
    body.streaming !== undefined ? `streaming ${body.streaming}` : null,
  ];
  return parts.filter(Boolean).join(" · ") || null;
}

function toRow(input: DebugInput, index: number): ActivityRow {
  const trend = input.kind === "trend";
  return {
    id: `${input.received_at}-${index}`,
    at: trend ? input.body?.source_ref?.timestamp ?? input.received_at : input.body?.timestamp ?? input.received_at,
    direction: "in",
    kind: "device",
    mission_id: null,
    label: trend ? "trend row" : String(input.body?.event_type ?? "envelope"),
    detail: trend ? trendDetail(input.body) : envelopeDetail(input.body),
    status: null,
    state_version: null,
    payload: { received_at: input.received_at, ...input.body },
    text: null,
  };
}

/** The intervention an input belongs to (trend rows carry it on source_ref). */
function inputIntervention(input: DebugInput): string | null {
  const body = input.body ?? {};
  return body.intervention_id ?? body.source_ref?.intervention_id ?? body.data?.intervention_id ?? null;
}

function DebugInputsFeed(props: { deviceId: string; interventionId: string | null }) {
  const [cookies] = useCookies(["access_token"]);
  const token: string | undefined = cookies.access_token;
  const [inputs, setInputs] = useState<DebugInput[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [hideHeartbeats, setHideHeartbeats] = useState(true);
  const [thisMissionOnly, setThisMissionOnly] = useState(true);
  const scoped = thisMissionOnly && props.interventionId !== null;

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const response = await axios.get<DebugInput[]>(
          `${CONSTANTS.HOST}api/missionagent/devices/${encodeURIComponent(props.deviceId)}/debug-inputs/`,
          { headers: { authorization: `Bearer ${token}` }, params: { limit: 500 } },
        );
        if (!cancelled) {
          setInputs(response.data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(errorMessage(err));
      }
    };
    void load();
    const timer = window.setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [token, props.deviceId]);

  const rows = inputs
    .filter((input) => !scoped || inputIntervention(input) === props.interventionId)
    .filter((input) => filter === "all" || (filter === "trend") === (input.kind === "trend"))
    .filter((input) => !(hideHeartbeats && input.body?.event_type === "device_status_received"))
    .map(toRow)
    // Trend rows arrive in batches (one pull a minute), so arrival order is not time order.
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at));

  return (
    <ActivityFeed
      title="Sentio inputs (debug)"
      caption={
        error
          ? `Could not load inputs: ${error}`
          : `Raw headless envelopes and pulled trend rows for ${
              scoped ? `mission ${props.interventionId}` : "every mission of this device"
            }, newest first (last 500, refreshed every ${
              POLL_MS / 1000
            } s). Click a row for the full input.`
      }
      rows={rows}
      emptyText={scoped ? "No inputs recorded for this mission in the last 500." : "No inputs recorded for this device yet."}
      action={
        <>
          <Switch size="small" exclusive value={filter} onChange={(_e, next: Filter | null) => next && setFilter(next)}>
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="events">Envelopes</ToggleButton>
            <ToggleButton value="trend">Trend</ToggleButton>
          </Switch>
          {props.interventionId !== null ? (
            <Switch
              size="small"
              exclusive
              value={thisMissionOnly ? "mission" : "device"}
              onChange={(_e, next: string | null) => next && setThisMissionOnly(next === "mission")}
            >
              <ToggleButton value="mission">This mission</ToggleButton>
              <ToggleButton value="device">All missions</ToggleButton>
            </Switch>
          ) : null}
          <Switch size="small">
            <ToggleButton value="heartbeats" selected={!hideHeartbeats} onChange={() => setHideHeartbeats(!hideHeartbeats)}>
              Heartbeats
            </ToggleButton>
          </Switch>
        </>
      }
    />
  );
}

export function DebugInputsButton(props: { deviceId: string; interventionId: string | null }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="small" variant="contained" sx={{ height: TOP_CONTROL_HEIGHT }} onClick={() => setOpen(true)}>
        Debug
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Sentio inputs for device {props.deviceId}</DialogTitle>
        <DialogContent>
          <Typography variant="overline" component="div" color="text.secondary">
            Input types
          </Typography>
          <Legend>
            {LEGEND.map(([name, text]) => (
              <Box key={name} sx={{ display: "contents" }}>
                <code>{name}</code>
                <span>{text}</span>
              </Box>
            ))}
          </Legend>
          {/* Mounted only while open, so it polls only while someone is looking. */}
          {open ? <DebugInputsFeed deviceId={props.deviceId} interventionId={props.interventionId} /> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
